import React, { useState, useEffect, useMemo } from "react";
import Modal from "../../elements/Modal";
import Dropdown from "../../elements/Dropdown";
import { Button } from "../../elements/Button";
import buttoncss from "../../css/button.module.css";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { Spinner } from "react-bootstrap";
import swal from "sweetalert";
import SuccessPopup from "../../elements/SuccessPopup";
import {
  getFormulaOptions,
  getPopulateFSFormulaOptions,
  getTemplateSocieConfigApiHandler,
  updateTemplateSocieConfigApiHandler,
  getStandaloneSocieConfigApiHandler,
  updateStandaloneSocieConfigApiHandler,
  getTemplateDetailsApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";

export const SOCIE_ROLE_OPTIONS = [
  { value: "opening", label: "Opening Balance" },
  { value: "changes", label: "Changes in Accounting Policy / Prior Period Errors" },
  { value: "restated", label: "Restated Opening Balance" },
  { value: "profit", label: "Profit for the Year" },
  { value: "oci", label: "Other Comprehensive Income" },
  { value: "tci", label: "Total Comprehensive Income" },
  { value: "movement", label: "Movement" },
  { value: "total", label: "Total" },
  { value: "closing", label: "Closing Balance" },
];

const getOptionString = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "string") return val.trim();
  if (typeof val === "object") {
    return (val.value || val.label || val.name || "").toString().trim();
  }
  return String(val).trim();
};

const EMPTY_ARRAY = [];
const IGNORE_FIELDS = [
  "particulars",
  "particular",
  "total",
  "currentyear",
  "previousyear",
  "isbold",
  "linetype",
  "_id",
  "formulatype",
  "lineitem",
  "reference",
  "references",
];

export default function SocieConfigModal({
  isOpen,
  onClose,
  groupingTemplateID,
  projectID,
  isTemplate = true,
  groupings = EMPTY_ARRAY,
  socieRows = EMPTY_ARRAY,
  socieColumns = EMPTY_ARRAY,
  onSaveSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formulaOptionsData, setFormulaOptionsData] = useState({});
  const [localGroupings, setLocalGroupings] = useState(groupings || EMPTY_ARRAY);
  const [selectedL1, setSelectedL1] = useState("");
  const [selectedL2, setSelectedL2] = useState("");
  const [selectedBsTie, setSelectedBsTie] = useState("");
  const [rowRoles, setRowRoles] = useState(EMPTY_ARRAY);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    if (Array.isArray(groupings) && groupings.length > 0) {
      setLocalGroupings(groupings);
    }
  }, [groupings]);

  // Determine row list: prioritize roles array length from config API, fallback to socieRows, or 13
  const totalRowCount = useMemo(() => {
    if (Array.isArray(rowRoles) && rowRoles.length > 0) {
      return rowRoles.length;
    }
    if (Array.isArray(socieRows) && socieRows.length > 0) {
      return socieRows.length;
    }
    return 13;
  }, [rowRoles, socieRows]);

  // Single-shot load formula options, groupings (if needed), and existing socieConfig on open
  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);

    const fetchOptions = isTemplate
      ? (groupingTemplateID ? getFormulaOptions(groupingTemplateID) : Promise.resolve({ data: {} }))
      : (projectID ? getPopulateFSFormulaOptions(projectID) : Promise.resolve({ data: {} }));

    const fetchConfig = isTemplate
      ? (groupingTemplateID ? getTemplateSocieConfigApiHandler({ groupingTemplateID }) : Promise.resolve({ data: {} }))
      : (projectID ? getStandaloneSocieConfigApiHandler({ projectID }) : Promise.resolve({ data: {} }));

    const fetchTemplateDetails =
      isTemplate && groupingTemplateID && (!groupings || groupings.length === 0)
        ? getTemplateDetailsApiHandler({ groupingTemplateID })
        : Promise.resolve({ data: {} });

    Promise.allSettled([fetchOptions, fetchConfig, fetchTemplateDetails])
      .then(([optsRes, configRes, tplRes]) => {
        if (optsRes.status === "fulfilled" && optsRes.value?.data?.success) {
          setFormulaOptionsData(optsRes.value.data.options || {});
        }

        if (tplRes.status === "fulfilled" && tplRes.value?.data?.success) {
          const tplGroupings = tplRes.value.data.groupingTemplate?.grouping;
          if (Array.isArray(tplGroupings) && tplGroupings.length > 0) {
            setLocalGroupings(tplGroupings);
          }
        }

        if (configRes.status === "fulfilled" && configRes.value?.data?.success) {
          const cfg = configRes.value.data.socieConfig;
          if (cfg) {
            setSelectedL1(getOptionString(cfg?.columnSource?.l1));
            setSelectedL2(getOptionString(cfg?.columnSource?.l2));
            setSelectedBsTie(getOptionString(cfg?.bsTie?.targetColumn));
            if (Array.isArray(cfg?.rowRoles)) {
              setRowRoles(cfg.rowRoles);
            }
          }
        }
      })
      .catch((err) => {
        console.error("Error loading SOCIE config / options:", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, isTemplate, groupingTemplateID, projectID]);

  // Extract Level 1 options from localGroupings and formula options grouping
  const l1Options = useMemo(() => {
    const sectionsSet = new Set();

    // 1. From template groupings hierarchy
    if (Array.isArray(localGroupings) && localGroupings.length > 0) {
      localGroupings.forEach((g) => {
        const name = getOptionString(g?.name || g?.label);
        if (name) {
          sectionsSet.add(name);
        }
      });
    }

    // 2. From formulaOptionsData.grouping (primary source for standalone FS)
    if (Array.isArray(formulaOptionsData?.grouping) && formulaOptionsData.grouping.length > 0) {
      formulaOptionsData.grouping.forEach((sec) => {
        const name = getOptionString(sec?.section);
        if (name) {
          sectionsSet.add(name);
        }
      });
    }

    // 3. Fallback: if no groupings found, check other modules
    if (sectionsSet.size === 0) {
      const modules = Object.keys(formulaOptionsData || {});
      modules.forEach((modKey) => {
        const secList = formulaOptionsData[modKey] || [];
        if (Array.isArray(secList)) {
          secList.forEach((item) => {
            const sec = getOptionString(item?.section);
            if (sec) {
              sectionsSet.add(sec);
            }
          });
        }
      });
    }

    const l1Str = getOptionString(selectedL1);
    if (l1Str) sectionsSet.add(l1Str);

    return Array.from(sectionsSet)
      .filter(Boolean)
      .map((sec) => ({
        label: sec,
        value: sec,
      }));
  }, [localGroupings, formulaOptionsData, selectedL1]);

  // Extract Level 2 options (filtered by selected Level 1)
  const l2Options = useMemo(() => {
    const subSectionsSet = new Set();
    const l1Norm = getOptionString(selectedL1).toLowerCase();

    // 1. If Level 1 is selected, find its Level 2 subgroups
    if (l1Norm) {
      // From template groupings hierarchy
      if (Array.isArray(localGroupings) && localGroupings.length > 0) {
        const found = localGroupings.find(
          (g) => getOptionString(g?.name || g?.label).toLowerCase() === l1Norm
        );
        if (found && Array.isArray(found.subgroup)) {
          found.subgroup.forEach((sub) => {
            const subName = getOptionString(sub?.name || sub?.label || sub);
            if (subName) {
              subSectionsSet.add(subName);
            }
          });
        }
      }

      // From formulaOptionsData.grouping
      if (Array.isArray(formulaOptionsData?.grouping)) {
        const found = formulaOptionsData.grouping.find(
          (item) => getOptionString(item?.section).toLowerCase() === l1Norm
        );
        if (found && Array.isArray(found.subSections)) {
          found.subSections.forEach((sub) => {
            const subName = getOptionString(sub?.subSection || sub?.name || sub);
            if (subName) {
              subSectionsSet.add(subName);
            }
          });
        }
      }

      // If still empty, check other modules
      if (subSectionsSet.size === 0) {
        const modules = Object.keys(formulaOptionsData || {});
        modules.forEach((modKey) => {
          if (modKey === "grouping") return;
          const secList = formulaOptionsData[modKey] || [];
          if (Array.isArray(secList)) {
            const found = secList.find(
              (item) => getOptionString(item?.section).toLowerCase() === l1Norm
            );
            if (found && Array.isArray(found.subSections)) {
              found.subSections.forEach((sub) => {
                const subName = getOptionString(sub?.subSection || sub?.name || sub);
                if (subName) {
                  subSectionsSet.add(subName);
                }
              });
            }
          }
        });
      }
    }

    // 2. If filtered list is empty or no L1 selected, include ALL Level 2 options from groupings
    if (subSectionsSet.size === 0) {
      if (Array.isArray(localGroupings) && localGroupings.length > 0) {
        localGroupings.forEach((g) => {
          if (Array.isArray(g.subgroup)) {
            g.subgroup.forEach((sub) => {
              const subName = getOptionString(sub?.name || sub?.label || sub);
              if (subName) {
                subSectionsSet.add(subName);
              }
            });
          }
        });
      }

      if (Array.isArray(formulaOptionsData?.grouping)) {
        formulaOptionsData.grouping.forEach((sec) => {
          if (Array.isArray(sec.subSections)) {
            sec.subSections.forEach((sub) => {
              const subName = getOptionString(sub?.subSection || sub?.name || sub);
              if (subName) {
                subSectionsSet.add(subName);
              }
            });
          }
        });
      }
    }

    const l2Str = getOptionString(selectedL2);
    if (l2Str) subSectionsSet.add(l2Str);

    return Array.from(subSectionsSet)
      .filter(Boolean)
      .map((sub) => ({
        label: sub,
        value: sub,
      }));
  }, [localGroupings, formulaOptionsData, selectedL1, selectedL2]);

  // Extract Balance Sheet Tie-Out Column options
  const bsTieOptions = useMemo(() => {
    const colSet = new Set();

    // 1. From passed socieColumns
    if (Array.isArray(socieColumns)) {
      socieColumns.forEach((c) => {
        const colName = getOptionString(typeof c === "string" ? c : c?.name);
        if (colName && !IGNORE_FIELDS.includes(colName.toLowerCase())) {
          colSet.add(colName);
        }
      });
    }

    // 2. From formulaOptions (specifically rows/cols under selected L2 or SOCIE module)
    const modules = Object.keys(formulaOptionsData || {});
    const l2Norm = getOptionString(selectedL2).toLowerCase();

    modules.forEach((modKey) => {
      const secList = formulaOptionsData[modKey] || [];
      if (Array.isArray(secList)) {
        secList.forEach((sec) => {
          if (Array.isArray(sec.subSections)) {
            sec.subSections.forEach((s) => {
              // If this subSection matches selected L2 (e.g. Other equity), add its rows (which become SOCIE columns)
              if (
                l2Norm &&
                getOptionString(s.subSection).toLowerCase() === l2Norm &&
                Array.isArray(s.rows)
              ) {
                s.rows.forEach((r) => {
                  const rName = getOptionString(r);
                  if (rName && !IGNORE_FIELDS.includes(rName.toLowerCase())) {
                    colSet.add(rName);
                  }
                });
              }

              // Also add cols defined in subSection
              if (Array.isArray(s.cols)) {
                s.cols.forEach((col) => {
                  const cName = getOptionString(typeof col === "string" ? col : col?.name);
                  if (cName && !IGNORE_FIELDS.includes(cName.toLowerCase())) {
                    colSet.add(cName);
                  }
                });
              }
            });
          }
        });
      }
    });

    const bsTieStr = getOptionString(selectedBsTie);
    if (bsTieStr) colSet.add(bsTieStr);

    return Array.from(colSet)
      .filter(Boolean)
      .map((c) => ({
        label: c,
        value: c,
      }));
  }, [socieColumns, formulaOptionsData, selectedL2, selectedBsTie]);

  // Get current role for position
  const getRoleForPosition = (pos) => {
    const match = rowRoles.find((r) => r.position === pos);
    return match ? match.role : "";
  };

  // Handle role change for a row
  const handleRoleChange = (pos, newRole) => {
    const roleStr = getOptionString(newRole);
    setRowRoles((prev) => {
      const existing = [...prev];
      const idx = existing.findIndex((r) => r.position === pos);
      if (idx !== -1) {
        existing[idx] = { position: pos, role: roleStr };
      } else {
        existing.push({ position: pos, role: roleStr });
      }
      return existing;
    });
  };

  // Save handler
  const handleSave = () => {
    const finalRowRoles = [];
    for (let i = 0; i < totalRowCount; i++) {
      finalRowRoles.push({
        position: i,
        role: getRoleForPosition(i),
      });
    }

    const payload = {
      columnSource: {
        l1: getOptionString(selectedL1),
        l2: getOptionString(selectedL2),
      },
      rowRoles: finalRowRoles,
      bsTie: {
        targetColumn: getOptionString(selectedBsTie),
      },
    };

    setSaving(true);

    const apiCall = isTemplate
      ? updateTemplateSocieConfigApiHandler({ groupingTemplateID }, payload)
      : updateStandaloneSocieConfigApiHandler({ projectID }, payload);

    apiCall
      .then((res) => {
        if (res?.data?.success === true) {
          setShowSuccessPopup(true);
          if (onSaveSuccess) onSaveSuccess(res?.data);
          setTimeout(() => {
            setShowSuccessPopup(false);
            onClose();
          }, 1500);
        } else {
          swal("Error", res?.data?.error || "Failed to save SOCIE configuration", "error");
        }
      })
      .catch((err) => {
        console.error("Save SOCIE config error:", err);
        swal(
          "Error",
          err?.response?.data?.error || err?.message || "Failed to save SOCIE configuration",
          "error"
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (!isOpen) return null;

  return (
    <>
    <Modal
      title="Configure SOCIE"
      headerActions={
        <CloseIcon
          style={{ cursor: "pointer", width: 16, height: 16 }}
          onClick={onClose}
        />
      }
      width="640px"
      bodycss="hide_scrollbar"
      bodyStyle={{
        background: "#fff",
        borderRadius: "0 0 10px 10px",
        padding: "24px 28px",
        maxHeight: "calc(75vh - 80px)",
        overflowY: "auto",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
      footerActions={
        <div style={{ display: "flex", justifyContent: "space-between" , gap: "12px", width: "100%" }}>
          <Button
            btnColor={buttoncss.secondary}
            style={{ backgroundColor: "transparent" }}
            label="Cancel"
            onClick={onClose}
            disabled={saving}
          />
          <Button
            btnColor={buttoncss.primary}
            label={saving ? "Saving..." : "Save Changes"}
            onClick={handleSave}
            disabled={saving || loading}
          />
        </div>
      }
    >
      <style>{`
        .hide_scrollbar::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
        .hide_scrollbar {
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
      `}</style>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "220px" }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {/* Column Source - Level 1 */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", fontWeight: "500", color: "#111111" }}>
              Select Level 1
            </label>
            <Dropdown
              placeHolder="Select Level 1"
              options={l1Options}
              selected={getOptionString(selectedL1)}
              selValue={getOptionString(selectedL1)}
              onSelect={(opt) => {
                const val = getOptionString(opt);
                setSelectedL1(val);
                setSelectedL2("");
              }}
              searchable={true}
              disableAutoSelect={true}
            />
          </div>

          {/* Column Source - Level 2 */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", fontWeight: "500", color: "#111111" }}>
              Select Level 2
            </label>
            <Dropdown
              placeHolder="Select Level 2"
              options={l2Options}
              selected={getOptionString(selectedL2)}
              selValue={getOptionString(selectedL2)}
              onSelect={(opt) => {
                const val = getOptionString(opt);
                setSelectedL2(val);
              }}
              searchable={true}
              disableAutoSelect={true}
            />
          </div>

          {/* Balance Sheet Tie-Out Column */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", fontWeight: "500", color: "#111111" }}>
              Balance Sheet Tie-Out Column
            </label>
            <Dropdown
              placeHolder="Select Balance Sheet Tie-Out Column"
              options={bsTieOptions}
              selected={getOptionString(selectedBsTie)}
              selValue={getOptionString(selectedBsTie)}
              onSelect={(opt) => {
                const val = getOptionString(opt);
                setSelectedBsTie(val);
              }}
              searchable={true}
              disableAutoSelect={true}
            />
          </div>

          {/* Row Types Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ fontSize: "13px", fontWeight: "500", color: "#111111" }}>
              Row Type Configuration
            </label>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                padding: "16px 18px",
                border: "1px solid #DCE6E2",
                borderRadius: "8px",
              }}
            >
              {Array.from({ length: totalRowCount }).map((_, index) => {
                const rowObj = socieRows?.[index];
                const rowName =
                  rowObj?.particular ||
                  rowObj?.particulars ||
                  rowObj?.name ||
                  rowObj?.row_header ||
                  (typeof rowObj === "string" ? rowObj : "");
                const currentRole = getRoleForPosition(index);
                const currentRoleObj = SOCIE_ROLE_OPTIONS.find((r) => r.value === currentRole);

                return (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      ...(index !== totalRowCount - 1
                        ? { borderBottom: "1px solid #eaeeed", paddingBottom: "20px" }
                        : {}),
                    }}
                  >
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#222" }}>
                      Row {index + 1}{rowName ? `: ${rowName}` : ""}
                    </span>
                    <label style={{ fontSize: "12px", fontWeight: "500", color: "#666" }}>
                      Select Row Type
                    </label>
                    <Dropdown
                      placeHolder="Select Row Type"
                      options={SOCIE_ROLE_OPTIONS}
                      selected={currentRoleObj ? currentRoleObj.label : ""}
                      selValue={currentRole || ""}
                      onSelect={(opt) => {
                        const val = opt?.value !== undefined ? opt.value : opt;
                        handleRoleChange(index, val);
                      }}
                      searchable={true}
                      disableAutoSelect={true}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Modal>
    <SuccessPopup
      isOpen={showSuccessPopup}
      message="SOCIE configuration saved successfully"
      onClose={() => {
        setShowSuccessPopup(false);
        onClose();
      }}
    />
    </>
  );
}
